/**
 * OrderBookChart - Visualizes the order book with horizontal bars.
 * Beginner mode shows simplified view with clear labels.
 * Advanced mode shows more detailed information.
 */

import { memo, useMemo, useCallback } from 'react'
import { HelpCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { OrderBook, Order } from '../../engine/types'

export interface OrderBookChartProps {
  /** The order book to display */
  orderBook: OrderBook
  /** Display mode - beginner uses simpler labels */
  mode: 'beginner' | 'advanced'
  /** Price to highlight (optional) */
  highlightedPrice?: number
  /** User's open order IDs to highlight as "Your order" */
  userOrderIds?: Set<string>
  /** Callback when a price level is clicked */
  onPriceClick?: (price: number) => void
  /** Optional className for the container */
  className?: string
}

/** Aggregated price level for display */
interface PriceLevel {
  price: number
  quantity: number
  orderCount: number
  hasUserOrder: boolean
}

/**
 * Aggregates orders by price level for visualization.
 */
function aggregateOrders(orders: Order[], userOrderIds?: Set<string>): PriceLevel[] {
  const levels = new Map<number, PriceLevel>()

  for (const order of orders) {
    const remaining = order.quantity - order.filledQuantity
    if (remaining <= 0) continue

    const isUserOrder = userOrderIds?.has(order.id) ?? false
    const existing = levels.get(order.price)
    if (existing) {
      existing.quantity += remaining
      existing.orderCount += 1
      if (isUserOrder) existing.hasUserOrder = true
    } else {
      levels.set(order.price, {
        price: order.price,
        quantity: remaining,
        orderCount: 1,
        hasUserOrder: isUserOrder,
      })
    }
  }

  return Array.from(levels.values())
}

/**
 * Single price level bar component.
 */
const PriceLevelBar = memo(function PriceLevelBar({
  level,
  side,
  maxQuantity,
  isHighlighted,
  onClick,
  mode,
}: {
  level: PriceLevel
  side: 'bid' | 'ask'
  maxQuantity: number
  isHighlighted: boolean
  onClick?: () => void
  mode: 'beginner' | 'advanced'
}) {
  const percentage = maxQuantity > 0 ? (level.quantity / maxQuantity) * 100 : 0
  const priceDisplay = Math.round(level.price * 100)

  const isBid = side === 'bid'
  const barColor = isBid ? 'bg-green-500/80' : 'bg-red-500/80'
  const hoverColor = isBid ? 'hover:bg-green-500' : 'hover:bg-red-500'

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative flex w-full items-center gap-2 rounded px-2 py-1 text-sm transition-colors',
        onClick ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default',
        isHighlighted && 'ring-2 ring-primary ring-offset-1',
        level.hasUserOrder && 'bg-primary/10 ring-1 ring-primary/50'
      )}
      aria-label={`${isBid ? 'Buy' : 'Sell'} order at ${priceDisplay}¢: ${level.quantity} shares${level.hasUserOrder ? ' (includes your order)' : ''}`}
    >
      {/* Price */}
      <span className="w-12 text-right font-mono tabular-nums text-foreground">
        {priceDisplay}¢
      </span>

      {/* Bar container */}
      <div className="relative h-5 flex-1 overflow-hidden rounded bg-muted/30">
        {/* Bar */}
        <div
          className={cn(
            'absolute h-full transition-all duration-200',
            barColor,
            hoverColor,
            isBid ? 'left-0' : 'right-0'
          )}
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />

        {/* Quantity label */}
        <span
          className={cn(
            'absolute top-1/2 -translate-y-1/2 px-2 text-xs font-medium',
            isBid ? 'left-0' : 'right-0',
            percentage > 50 ? 'text-white' : 'text-foreground'
          )}
        >
          {level.quantity}
        </span>
      </div>

      {/* Order count (advanced mode) */}
      {mode === 'advanced' && (
        <span className="w-8 text-left text-xs text-muted-foreground">
          ({level.orderCount})
        </span>
      )}

      {/* User order indicator */}
      {level.hasUserOrder && (
        <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground shadow-sm">
          You
        </span>
      )}

      {/* Tooltip on hover */}
      <div
        className={cn(
          'pointer-events-none absolute z-10 rounded bg-popover px-2 py-1 text-xs shadow-md',
          'opacity-0 transition-opacity group-hover:opacity-100',
          isBid ? 'left-full ml-2' : 'right-full mr-2'
        )}
        role="tooltip"
      >
        {mode === 'beginner' ? (
          <span>
            {isBid
              ? `Someone wants to buy ${level.quantity} shares at ${priceDisplay}¢`
              : `Someone wants to sell ${level.quantity} shares at ${priceDisplay}¢`}
          </span>
        ) : (
          <span>
            {level.quantity} shares @ ${level.price.toFixed(2)} ({level.orderCount}{' '}
            {level.orderCount === 1 ? 'order' : 'orders'})
          </span>
        )}
      </div>
    </button>
  )
})

/**
 * OrderBookChart displays bids and asks as horizontal bar charts.
 */
export const OrderBookChart = memo(function OrderBookChart({
  orderBook,
  mode,
  highlightedPrice,
  userOrderIds,
  onPriceClick,
  className,
}: OrderBookChartProps) {
  // Aggregate orders into price levels
  // Bids sorted ascending so highest bid (best bid) is at bottom, next to asks
  const bidLevels = useMemo(
    () => aggregateOrders(orderBook.bids, userOrderIds).sort((a, b) => a.price - b.price),
    [orderBook.bids, userOrderIds]
  )

  const askLevels = useMemo(
    () => aggregateOrders(orderBook.asks, userOrderIds).sort((a, b) => a.price - b.price),
    [orderBook.asks, userOrderIds]
  )

  // Find max quantity for scaling bars
  const maxQuantity = useMemo(() => {
    const allQuantities = [...bidLevels, ...askLevels].map((l) => l.quantity)
    return allQuantities.length > 0 ? Math.max(...allQuantities) : 0
  }, [bidLevels, askLevels])

  // Calculate spread
  // Best bid is now the last item (highest price), best ask is first item (lowest price)
  const spread = useMemo(() => {
    const bestBid = bidLevels.length > 0 ? bidLevels[bidLevels.length - 1].price : null
    const bestAsk = askLevels[0]?.price ?? null
    if (bestBid !== null && bestAsk !== null) {
      return Math.round((bestAsk - bestBid) * 100)
    }
    return null
  }, [bidLevels, askLevels])

  const handlePriceClick = useCallback(
    (price: number) => {
      onPriceClick?.(price)
    },
    [onPriceClick]
  )

  const hasOrders = bidLevels.length > 0 || askLevels.length > 0

  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-4', className)}
      role="region"
      aria-label="Order book"
    >
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">
          {mode === 'beginner' ? 'Order Book' : 'Order Book (Depth)'}
        </h3>
        {spread !== null && (
          <span className="text-xs text-muted-foreground">
            Spread: {spread}¢
          </span>
        )}
      </div>

      {!hasOrders ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No orders yet. Be the first to place an order!
        </div>
      ) : (
        <div className="space-y-4">
          {/* Bids (buy orders) */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <span className="text-xs font-medium text-muted-foreground">
                {mode === 'beginner'
                  ? 'People willing to buy'
                  : `Bids (${bidLevels.length})`}
              </span>
              {mode === 'beginner' && (
                <span className="rounded bg-green-500/10 px-1.5 py-0.5 text-[10px] font-medium text-green-600 dark:text-green-400">
                  Bids
                </span>
              )}
            </div>
            <div className="space-y-0.5">
              {bidLevels.length > 0 ? (
                bidLevels.slice(-5).map((level) => (
                  <PriceLevelBar
                    key={level.price}
                    level={level}
                    side="bid"
                    maxQuantity={maxQuantity}
                    isHighlighted={highlightedPrice === level.price}
                    onClick={onPriceClick ? () => handlePriceClick(level.price) : undefined}
                    mode={mode}
                  />
                ))
              ) : (
                <p className="py-2 text-xs text-muted-foreground">
                  No buy orders
                </p>
              )}
            </div>
          </div>

          {/* Spread indicator */}
          {spread !== null && (
            <div className="flex items-center justify-center gap-2 border-y border-dashed border-border py-2">
              <span className="text-xs text-muted-foreground">
                {mode === 'beginner'
                  ? `Gap between buyers and sellers: ${spread}¢`
                  : `Spread: ${spread}¢`}
              </span>
            </div>
          )}

          {/* Asks (sell orders) */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-500" />
              <span className="text-xs font-medium text-muted-foreground">
                {mode === 'beginner'
                  ? 'People willing to sell'
                  : `Asks (${askLevels.length})`}
              </span>
              {mode === 'beginner' && (
                <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
                  Asks
                </span>
              )}
            </div>
            <div className="space-y-0.5">
              {askLevels.length > 0 ? (
                askLevels.slice(0, 5).map((level) => (
                  <PriceLevelBar
                    key={level.price}
                    level={level}
                    side="ask"
                    maxQuantity={maxQuantity}
                    isHighlighted={highlightedPrice === level.price}
                    onClick={onPriceClick ? () => handlePriceClick(level.price) : undefined}
                    mode={mode}
                  />
                ))
              ) : (
                <p className="py-2 text-xs text-muted-foreground">
                  No sell orders
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legend for beginners */}
      {mode === 'beginner' && hasOrders && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            Click a price to use it in your order. The gap between green and red
            is called the &ldquo;spread&rdquo; &mdash; a smaller spread means more agreement on price.
          </p>

          {/* Educational section */}
          <details className="mt-3">
            <summary className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <HelpCircle className="h-3 w-3" />
              <span>Why are these prices here?</span>
            </summary>
            <div className="mt-2 space-y-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              <p>
                <strong className="text-foreground">Prices = Probability:</strong>{' '}
                Each price represents what traders think is the probability this event will happen. A price of 60¢ means about 60% chance.
              </p>
              <p>
                <strong className="text-foreground">Green (Buy) Orders:</strong>{' '}
                People who think the price is too low. They want to buy because they believe the real probability is higher.
              </p>
              <p>
                <strong className="text-foreground">Red (Sell) Orders:</strong>{' '}
                People who think the price is too high. They want to sell because they believe the real probability is lower.
              </p>
              <p>
                <strong className="text-foreground">The Gap (Spread):</strong>{' '}
                Where buyers and sellers disagree. A narrow gap means strong consensus; a wide gap means uncertainty.
              </p>
            </div>
          </details>
        </div>
      )}
    </div>
  )
})
